import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

export const usePageTracking = () => {
  const location = useLocation();
  const sessionId = useRef(localStorage.getItem('analytics_session_id') || crypto.randomUUID());
  const startTime = useRef(Date.now());
  const maxScroll = useRef(0);
  const interactions = useRef<Array<{ type: string; element: string; timestamp: number; text?: string }>>([]);

  useEffect(() => {
    const activeSessionId = sessionId.current;

    // Save session ID
    localStorage.setItem('analytics_session_id', activeSessionId);

    // Reset metrics for new page
    startTime.current = Date.now();
    maxScroll.current = 0;
    interactions.current = [];

// Track page view start
const trackPageView = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    const browserUA = (navigator.userAgent || '').slice(0, 500);
    const osVal = (navigator.platform || '').slice(0, 100);
    const utmRaw = {
      source: urlParams.get('utm_source'),
      medium: urlParams.get('utm_medium'),
      campaign: urlParams.get('utm_campaign'),
      content: urlParams.get('utm_content'),
      term: urlParams.get('utm_term')
    } as const;
    const hasAnyUtm = Object.values(utmRaw).some(v => v != null && v !== '');

    const payload: {
      sessionId: string;
      pagePath: string;
      pageTitle: string;
      referrer: string;
      deviceType: 'mobile' | 'tablet' | 'desktop';
      browser: string;
      os: string;
      utmParams?: typeof utmRaw;
    } = {
      sessionId: activeSessionId,
      pagePath: location.pathname,
      pageTitle: document.title.slice(0, 200),
      referrer: document.referrer,
      deviceType: getDeviceType(),
      browser: browserUA,
      os: osVal,
    };

    if (hasAnyUtm) {
      payload.utmParams = utmRaw;
    }

    await supabase.functions.invoke('track-page-view', { body: payload });
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

    // Defer tracking to idle time for better performance
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => trackPageView(), { timeout: 2000 });
    } else {
      setTimeout(() => trackPageView(), 0);
    }

    // Track scroll depth
    let scrollFrame = 0;
    const handleScroll = () => {
      if (scrollFrame) return;

      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = 0;
        const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollableHeight <= 0) return;

        const scrollPercentage = Math.round((window.scrollY / scrollableHeight) * 100);
        if (scrollPercentage > maxScroll.current) {
          maxScroll.current = Math.min(scrollPercentage, 100);
        }
      });
    };

    // Track interactions (clicks on important elements)
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const clickableElement = target.closest('button, a[href], [role="button"]');
      
      if (clickableElement && interactions.current.length < 40) {
        interactions.current.push({
          type: 'click',
          element: clickableElement.tagName,
          timestamp: Date.now() - startTime.current,
          text: clickableElement.textContent?.slice(0, 50)
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('click', handleClick);

    // Send final metrics on unmount (non-blocking)
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick);
      if (scrollFrame) {
        window.cancelAnimationFrame(scrollFrame);
      }

      const timeOnPage = Math.round((Date.now() - startTime.current) / 1000);
      
      // Only send if user spent more than 2 seconds on page (avoid noise)
      if (timeOnPage > 2) {
        // Defer to idle time or use sendBeacon for exit tracking
        const payload = {
          sessionId: activeSessionId,
          pagePath: location.pathname,
          timeOnPage,
          scrollDepth: maxScroll.current,
          interactions: interactions.current
        };
        
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            import('@/integrations/supabase/client')
              .then(({ supabase }) => supabase.functions.invoke('track-page-view', { body: payload }))
              .catch(() => {});
          });
        } else {
          setTimeout(() => {
            import('@/integrations/supabase/client')
              .then(({ supabase }) => supabase.functions.invoke('track-page-view', { body: payload }))
              .catch(() => {});
          }, 0);
        }
      }
    };
  }, [location.pathname]);
};
